import { Table } from '@/components/ui';
import Empty from '@/components/ui/empty';
import { useOrders } from '@/features/account/api/get-orders';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { FilledOrder, useOrdersStore } from '@/store/use-orders-store';
import { CapitalStatus, OrderStatus, OrderType } from '@/types';
import { cn } from '@/utils/cn';
import { formatBigInt, parseDecimalToBigInt } from '@/utils/value-format';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useCapitalPlans } from '../api/get-capital-plans';
import { useFetchAccount } from '@/features/account/api/get-account';
import Symbol from '@/features/account/components/open-orders/symbol';
import SettledFunding from '@/features/account/components/orders-history/settled-funding';
import RealizedPnl from '@/features/account/components/orders-history/realized-pnl';
import {
  handleOrderForStore,
  orderResponseToOrder,
} from '@/utils/handle-store-data';

export default function LiquidationTable() {
  const { t } = useTranslation();
  const { data: orders, isLoading } = useOrders();

  useEffect(() => {
    if (!orders) return;
    orders.forEach((res) => {
      const order = orderResponseToOrder(res);
      handleOrderForStore(order, { skipFill: true });
    });
  }, [orders]);

  const { address, capitalStatus } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  const data = useOrdersStore(
    useShallow((state) => {
      const { orders, filledOrderIds } = state.getOrders(address);
      return Array.from(filledOrderIds)
        .filter(
          (id) =>
            orders[id]?.status === OrderStatus.FILLED &&
            orders[id].realizedPnl !== 0n,
        )
        .sort(
          (a, b) =>
            (orders[b] as FilledOrder).lastFilledTime -
            (orders[a] as FilledOrder).lastFilledTime,
        );
    }),
  );

  const { data: accountResponse } = useFetchAccount();
  const { data: capitalPlans } = useCapitalPlans();

  const { startingBalance, maxDailyLossPct } = useMemo(() => {
    if (!capitalPlans || !accountResponse?.capital)
      return { startingBalance: 0n, maxDailyLossPct: 0 };
    const activeCapitalPlan = accountResponse.capital.activeCapitalPlan;
    const activePlanMetadata = capitalPlans?.find(
      (plan) => plan.name === activeCapitalPlan,
    );
    if (!activePlanMetadata) return { startingBalance: 0n, maxDailyLossPct: 0 };
    return {
      maxDailyLossPct: Number(activePlanMetadata.metadata.max_daily_loss),
      startingBalance: parseDecimalToBigInt(
        (Number(activePlanMetadata.metadata.account_size) / 100).toString(),
        COLLATERAL_DECIMALS,
      ),
    };
  }, [capitalPlans, accountResponse?.capital?.activeCapitalPlan]);

  const columns = useMemo<ColumnDef<string>[]>(
    () => [
      {
        id: 'symbol',
        cell: ({ row }) => (
          // NOTE: close so opposite size
          <Symbol orderId={row.original} skipSubscription skipLink />
        ),
        header: t('ordersHistoryTable.market'),
      },
      {
        id: 'settledFunding',
        cell: ({ row }) => <SettledFunding orderId={row.original} />,
        header: t('ordersHistoryTable.funding'),
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        id: 'realizedPnl',
        cell: ({ row }) => (
          <div className="mt-3">
            <RealizedPnl orderId={row.original} />
          </div>
        ),
        header: t('ordersHistoryTable.pnl'),
        sortDescFirst: false,
        sortUndefined: 'last',
        meta: { width: 'flex justify-end' },
      },
    ],
    [t],
  );

  const maxDrawdownLimit = parseDecimalToBigInt(
    accountResponse?.capital?.maxDrawdownLimit,
    COLLATERAL_DECIMALS,
  );

  const maxDailyLossLimit = parseDecimalToBigInt(
    accountResponse?.capital?.maxDailyLossLimit,
    COLLATERAL_DECIMALS,
  );

  const priorDayAccountValue = parseDecimalToBigInt(
    accountResponse?.capital?.priorDayAccountValue ??
      accountResponse?.capital?.priorDayBalance,
    COLLATERAL_DECIMALS,
  );
  const priorDayMaxDailyLossLimit =
    (priorDayAccountValue * (100n - BigInt(maxDailyLossPct))) / 100n;

  const withdrawnAmount = priorDayMaxDailyLossLimit - maxDailyLossLimit;
  const accountValue = useCurrentAccount((state) => state.value.bigint);

  const maxDrawdown = startingBalance - maxDrawdownLimit;
  const maxDailyLoss =
    priorDayAccountValue - maxDailyLossLimit - withdrawnAmount;

  const dailyLoss = priorDayAccountValue - accountValue - withdrawnAmount;
  const drawdown =
    startingBalance > accountValue ? startingBalance - accountValue : 0n;

  return (
    <div className="-mx-5 bg-background px-5 sm:-mx-8 sm:px-8">
      <Table
        data={data}
        columns={columns}
        loading={isLoading}
        isInfiniteScroll
        infiniteScrollEstimateRowSize={57}
        className="h-auto max-h-72 [&_td:first-child]:pl-0 [&_td:last-child]:pr-0 [&_th:first-child]:pl-0 [&_th:last-child]:pr-0 [&_th]:py-2"
        getRowClassName={(id) => {
          const { orders } = useOrdersStore.getState().getOrders(address);
          return cn(
            orders[id].orderType === OrderType.LIQUIDATION &&
              'border-y border-red bg-red/5 hover:bg-red/10',
          );
        }}
        emptyComponent={
          <Empty
            title={t('ordersHistoryTable.noOrdersFound')}
            description={t('ordersHistoryTable.noOrdersDescription')}
          />
        }
      />
      {!isLoading && (
        <div className="mt-4 flex items-center justify-between">
          <p>
            {t('capitalResultModal.total')}{' '}
            {[
              CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS,
              CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS,
            ].includes(capitalStatus) && t('capitalResultModal.24h')}
          </p>
          <p>
            <span className="text-red">
              {(() => {
                switch (capitalStatus) {
                  case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
                  case CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS:
                    return formatBigInt(
                      (dailyLoss > maxDailyLoss ? dailyLoss : maxDailyLoss) *
                        -1n,
                      COLLATERAL_DECIMALS,
                      {
                        digits: 2,
                      },
                    );
                  case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
                  case CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN:
                    return formatBigInt(
                      (drawdown > maxDrawdown ? drawdown : maxDrawdown) * -1n,
                      COLLATERAL_DECIMALS,
                      {
                        digits: 2,
                      },
                    );
                }
              })()}
            </span>{' '}
            /{' '}
            {(() => {
              switch (capitalStatus) {
                case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
                case CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS:
                  return (
                    formatBigInt(maxDailyLoss * -1n, COLLATERAL_DECIMALS, {
                      digits: 2,
                    }) +
                    ' ' +
                    t('capitalResultModal.dailyLoss')
                  );
                case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
                case CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN:
                  return (
                    formatBigInt(maxDrawdown * -1n, COLLATERAL_DECIMALS, {
                      digits: 2,
                    }) +
                    ' ' +
                    t('capitalResultModal.drawdown')
                  );
              }
            })()}
          </p>
        </div>
      )}
    </div>
  );
}
